import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { Avatar } from '../components/Avatar';
import { CreateInvoiceModal } from '../components/CreateInvoiceModal';
import type { ClientSummary, InvoiceStatus, InvoiceSummary } from '../types';

type ListResponse = {
  items: Array<
    InvoiceSummary & {
      client: { id: string; name: string; type: string } | null;
      case: { id: string; caseNumber: string; area: string | null } | null;
    }
  >;
  summary: {
    totalCount: number;
    paidAgorot: number;
    outstandingAgorot: number;
    outstandingCount: number;
    unbilledMinutes: number;
  };
};

const STATUS_BADGE: Record<InvoiceStatus, { cls: string; key: string }> = {
  draft: { cls: 'b-pending', key: 'status.draft' },
  issued: { cls: 'b-pending', key: 'status.issued' },
  paid: { cls: 'b-open', key: 'status.paid' },
  cancelled: { cls: 'b-closed', key: 'status.cancelled' },
};

function formatIls(agorot: number): string {
  const shekels = agorot / 100;
  return shekels >= 1000
    ? `₪${(shekels / 1000).toFixed(1)}K`
    : `₪${shekels.toFixed(2)}`;
}

export function Billing({ searchQuery }: { searchQuery: string }) {
  const { t } = useTranslation(['billing', 'common']);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState<ClientSummary[]>([]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch<ListResponse>('/api/invoices');
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Pull clients once for the create-invoice dropdown
    apiFetch<{ items: ClientSummary[] }>('/api/clients')
      .then((d) => setClients(d.items))
      .catch(() => setClients([]));
  }, []);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter(
      (i) =>
        (i.greeninvoiceNumber ?? '').toLowerCase().includes(q) ||
        (i.client?.name ?? '').toLowerCase().includes(q) ||
        (i.case?.caseNumber ?? '').toLowerCase().includes(q),
    );
  }, [data, searchQuery]);

  if (loading && !data) return <div className="empty">{t('list.loading')}</div>;
  if (error) return <div className="empty">{t('common:errors.with_message', { message: error })}</div>;
  if (!data) return null;

  const monthName = new Date().toLocaleString('he', { month: 'long' });
  const year = new Date().getFullYear();
  const totalAgorot = data.summary.paidAgorot + data.summary.outstandingAgorot;

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('metrics.total')}</div>
          <div className="metric-val">{formatIls(totalAgorot)}</div>
          <div className="metric-sub">{t('metrics.this_month_label', { month: monthName, year })}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.paid')}</div>
          <div className="metric-val">{formatIls(data.summary.paidAgorot)}</div>
          {totalAgorot > 0 && (
            <div className="metric-sub up">
              {Math.round((data.summary.paidAgorot / totalAgorot) * 100)}%
            </div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.outstanding')}</div>
          <div className="metric-val">{formatIls(data.summary.outstandingAgorot)}</div>
          <div className="metric-sub down">
            {t('metrics.outstanding_invoices', { count: data.summary.outstandingCount })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.hours')}</div>
          <div className="metric-val">
            {Math.round((data.summary.unbilledMinutes / 60) * 10) / 10}
          </div>
          <div className="metric-sub">
            {t('metrics.unbilled_hint', { defaultValue: 'unbilled' })}
          </div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">
          <span>{t('list.title')}</span>
          <button className="btn-link" onClick={() => setCreating(true)}>
            + {t('actions.create_invoice')}
          </button>
        </div>
        {filteredItems.length === 0 ? (
          <div className="empty">
            {searchQuery.trim() ? t('list.empty_search') : t('list.empty')}
          </div>
        ) : (
          filteredItems.map((inv) => {
            const badge = STATUS_BADGE[inv.status];
            const clientName = inv.client?.name ?? '—';
            return (
              <div
                key={inv.id}
                className="billing-row"
                onClick={() => inv.documentUrl && window.open(inv.documentUrl, '_blank', 'noopener')}
                style={{ cursor: inv.documentUrl ? 'pointer' : 'default' }}
              >
                <Avatar name={clientName} size="md" />
                <div className="billing-info">
                  <div className="billing-name">
                    {clientName}
                    {inv.greeninvoiceNumber && (
                      <span style={{ color: 'var(--text3)', fontWeight: 400, marginInlineStart: 8 }}>
                        #{inv.greeninvoiceNumber}
                      </span>
                    )}
                  </div>
                  <div className="billing-case">
                    {inv.case && t('list.for_case', { caseNumber: inv.case.caseNumber })}
                    {inv.case && ' · '}
                    {inv.paidAt
                      ? t('list.paid_on', { date: inv.paidAt.slice(0, 10) })
                      : inv.dueAt
                        ? t('list.due', { date: inv.dueAt.slice(0, 10) })
                        : t('list.no_due')}
                  </div>
                </div>
                <div style={{ textAlign: 'end' }}>
                  <div className="billing-amount">{formatIls(inv.totalAgorot)}</div>
                  <span className={`badge ${badge.cls}`} style={{ marginTop: 4 }}>
                    {t(badge.key)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {creating && (
        <CreateInvoiceModal
          clients={clients}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
